import { HttpClient } from '@angular/common/http';
import { computed, effect, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { firstValueFrom, fromEvent } from 'rxjs';

import { ACCESS_STORE } from '@core-interactions/stores/access.store';
import { ENVIRONMENT } from '@env/environment';
import { APP_STORE } from '@stores/app.store';
import { USER_STORE } from '@stores/user.store';
import { ZendeskUserContext } from '@zendesk/models/zendesk-user-context';
import { ZENDESK_STORE } from '@zendesk/stores/zendesk.store';
import {
  closeZendeskMessenger,
  formatIdNameTag,
  loginZendeskUser,
  logoutZendeskUser,
  onZendeskMessengerClose,
  onZendeskMessengerOpen,
  onZendeskUnreadMessages,
  openZendeskMessenger,
  setZendeskConversationFields,
  setZendeskConversationTags,
  ZENDESK_APP_VERSION
} from '@zendesk/utils/zendesk-messenger';

@Injectable()
export class ZendeskService {
  readonly #httpClient = inject(HttpClient);
  readonly #zendeskStore = inject(ZENDESK_STORE);
  readonly #userStore = inject(USER_STORE);
  readonly #appStore = inject(APP_STORE);
  readonly #accessStore = inject(ACCESS_STORE);

  // Registered by the toggle button's host component so this listener can
  // ignore its clicks without fighting the button's own open/close logic.
  #toggleElement: HTMLElement | null = null;

  private readonly userContext = computed((): ZendeskUserContext => {
    const clientId = this.#appStore.selectedClientId() || null;
    const account = this.#appStore.selectedAccount();
    const clientFromAccess = clientId
      ? this.#accessStore.clients().entities.find(c => c.id === clientId)
      : undefined;

    return {
      email: this.#userStore.data()?.email,
      clientId,
      clientName: account?.client?.name ?? clientFromAccess?.name
    };
  });

  private readonly login = this.guardedOnce(() =>
    loginZendeskUser(() => this.fetchMessagingToken())
  );

  // Survives logout (the widget keeps the listeners), unlike `login` this is never reset.
  private readonly launcherListeners = this.guardedOnce(() =>
    Promise.all([
      onZendeskUnreadMessages(count => {
        this.#zendeskStore.setUnreadCount(count);
      }),
      onZendeskMessengerOpen(() => this.#zendeskStore.setOpen(true)),
      onZendeskMessengerClose(() => this.#zendeskStore.setOpen(false))
    ]).then(dispatched => dispatched.every(Boolean))
  );

  initialize(): void {
    effect(() => {
      this.#userStore.data();
      this.#appStore.selectedClientId();
      this.#accessStore.clients();
      void this.syncWidget();
    });

    fromEvent<MouseEvent>(document, 'mousedown', { capture: true })
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (!this.#zendeskStore.isOpen()) {
          return;
        }
        const target = event.target as Node | null;
        if (target && this.#toggleElement?.contains(target)) {
          return;
        }
        this.close();
      });
  }

  async syncWidget(): Promise<void> {
    if (!this.#zendeskStore.canShowWidget()) {
      this.close();
      this.#zendeskStore.reset();
      return;
    }

    void this.login.run();
    void this.launcherListeners.run();
    this.applyContext();
  }

  open(): void {
    openZendeskMessenger();
  }

  close(): void {
    closeZendeskMessenger();
  }

  toggle(): void {
    if (this.#zendeskStore.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  registerToggleElement(element: HTMLElement | null): void {
    this.#toggleElement = element;
  }

  logoutUser(): void {
    this.login.reset();
    this.#zendeskStore.reset();
    logoutZendeskUser();
  }

  //#region Private methods

  // Dispatches at most once; reset() re-arms it without clobbering an in-flight dispatch.
  private guardedOnce(dispatch: () => Promise<boolean>): {
    run: () => Promise<void>;
    reset: () => void;
  } {
    let state: 'idle' | 'inflight' | 'done' = 'idle';
    return {
      run: async (): Promise<void> => {
        if (state !== 'idle') {
          return;
        }
        state = 'inflight';
        const dispatched = await dispatch();
        if (state === 'inflight') {
          state = dispatched ? 'done' : 'idle';
        }
      },
      reset: (): void => {
        state = 'idle';
      }
    };
  }

  private applyContext(): void {
    const context = this.userContext();

    const tags = [
      'app:analytics',
      formatIdNameTag('env', undefined, ENVIRONMENT.moduleName),
      formatIdNameTag('client', context.clientId, context.clientName)
    ].filter((tag): tag is string => Boolean(tag));

    setZendeskConversationTags(tags);

    setZendeskConversationFields([
      { id: ENVIRONMENT.zendesk.userEmailFieldId, value: context.email },
      { id: ENVIRONMENT.zendesk.clientFieldId, value: context.clientName },
      {
        id: ENVIRONMENT.zendesk.appFieldId,
        value: ENVIRONMENT.zendesk.appFieldValue
      },
      {
        id: ENVIRONMENT.zendesk.appVersionFieldId,
        value: ZENDESK_APP_VERSION
      },
      {
        id: ENVIRONMENT.zendesk.envFieldId,
        value: ENVIRONMENT.zendesk.envFieldValue
      }
    ]);
  }

  private fetchMessagingToken(): Promise<string | null> {
    return firstValueFrom(
      this.#httpClient.post<{ token: string }>(
        `${ENVIRONMENT.coreApi.endpoint}/zendesk/messaging-token`,
        null
      )
    )
      .then(response => response?.token ?? null)
      .catch(() => null);
  }

  //#endregion
}
